import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  PlusIcon, 
  MagnifyingGlassIcon, 
  PencilIcon, 
  TrashIcon,
  EyeIcon,
  UserCircleIcon,
  ShieldCheckIcon,
  XCircleIcon
} from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { useAuth } from '../../contexts/AuthContext'

const USER_ROLES = {
  // System tier
  super_admin: { label: 'Super Admin', color: 'bg-red-100 text-red-800', tier: 'system' },
  system: { label: 'System', color: 'bg-red-100 text-red-800', tier: 'system' },
  admin: { label: 'Administrator', color: 'bg-red-100 text-red-800', tier: 'system' },
  // Group tier
  org_admin: { label: 'Org Admin', color: 'bg-purple-100 text-purple-800', tier: 'group' },
  user_manager: { label: 'User Manager', color: 'bg-purple-100 text-purple-800', tier: 'group' },
  compliance_manager: { label: 'Compliance Manager', color: 'bg-amber-100 text-amber-800', tier: 'group' },
  compliance_officer: { label: 'Compliance Officer', color: 'bg-amber-100 text-amber-800', tier: 'group' },
  aml_analyst: { label: 'AML Analyst', color: 'bg-amber-100 text-amber-800', tier: 'group' },
  governance_manager: { label: 'Governance Manager', color: 'bg-blue-100 text-blue-800', tier: 'group' },
  executive: { label: 'Executive', color: 'bg-blue-100 text-blue-800', tier: 'group' },
  auditor: { label: 'Auditor', color: 'bg-blue-100 text-blue-800', tier: 'group' },
  // Venue tier
  gaming_manager: { label: 'Gaming Manager', color: 'bg-green-100 text-green-800', tier: 'venue' },
  duty_manager: { label: 'Duty Manager', color: 'bg-green-100 text-green-800', tier: 'venue' },
  gaming_staff: { label: 'Gaming Staff', color: 'bg-green-100 text-green-800', tier: 'venue' },
  payout_officer: { label: 'Payout Officer', color: 'bg-green-100 text-green-800', tier: 'venue' },
  venue_admin: { label: 'Venue Admin', color: 'bg-green-100 text-green-800', tier: 'venue' },
  operator: { label: 'Operator', color: 'bg-indigo-100 text-indigo-800', tier: 'venue' },
  viewer: { label: 'Viewer', color: 'bg-indigo-100 text-indigo-800', tier: 'venue' },
}

// Fallback for roles not in the map
const getRoleDisplay = (role) => USER_ROLES[role] || { label: role, color: 'bg-gray-100 text-gray-800', tier: 'other' }

const USER_STATUS = {
  active: { label: 'Active', color: 'bg-green-100 text-green-800' },
  inactive: { label: 'Inactive', color: 'bg-gray-100 text-gray-800' },
}

// Permissions based on role (UI utility)
const getRolePermissions = (role) => {
  const permissionMap = {
    admin: ['CREATE_RULES', 'EDIT_RULES', 'DELETE_RULES', 'MANAGE_CASES', 'VIEW_AUDIT', 'MANAGE_USERS'],
    operator: ['CREATE_RULES', 'EDIT_RULES', 'MANAGE_CASES', 'VIEW_AUDIT'],
    viewer: ['VIEW_CASES', 'VIEW_AUDIT'],
    supervisor: ['CREATE_RULES', 'EDIT_RULES', 'MANAGE_CASES'],
    compliance_manager: ['CREATE_RULES', 'EDIT_RULES', 'MANAGE_CASES', 'VIEW_AUDIT'],
    compliance_officer: ['VIEW_CASES', 'UPDATE_CASES', 'VIEW_AUDIT']
  }
  return permissionMap[role] || ['VIEW_CASES']
}

function Users() {
  const navigate = useNavigate()
  const { user, hasPermission } = useAuth()
  const canSeeSuperAdminRole = Boolean(
    user &&
      (user.role === 'admin' ||
        user.role === 'super_admin' ||
        hasPermission('platform:manage'))
  )
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState('ALL')
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)

  const roleFilterEntries = Object.entries(USER_ROLES).filter(
    ([key]) => key !== 'super_admin' || canSeeSuperAdminRole
  )

  useEffect(() => {
    if (roleFilter === 'super_admin' && !canSeeSuperAdminRole) setRoleFilter('ALL')
  }, [roleFilter, canSeeSuperAdminRole])

  // Load users from API
  useEffect(() => {
    loadUsers()
  }, [])

  const loadUsers = async () => {
    try {
      setLoading(true)
      setError(null)

      // Fetch users and reference data in parallel
      const [usersRes, refRes] = await Promise.all([
        authFetch(getLocalApiUrl('/api/admin/users')),
        authFetch(getLocalApiUrl('/api/admin/reference-data'))
      ])
      const result = await usersRes.json()
      const refResult = await refRes.json()

      if (!result.success) {
        throw new Error(result.error || 'Failed to load users')
      }

      const refData = refResult.success ? refResult.data : { teams: [], locations: [] }

      // Transform database users to match UI format
      const transformedUsers = (result.data || []).map((user, index) => {
        // Split name into first and last (or use full name for both if no space)
        const nameParts = (user.name || '').split(' ')
        const firstName = nameParts[0] || user.username
        const lastName = nameParts.slice(1).join(' ') || ''

        return {
          id: user.username, // Use username as unique ID
          username: user.username,
          email: user.email || `${user.username}@company.com`,
          firstName: firstName,
          lastName: lastName,
          phone: user.phone || '',
          location: user.location || (refData.locations[index % refData.locations.length] || ''),
          role: user.role || 'viewer',
          status: user.active ? 'active' : 'inactive',
          team: user.team || (refData.teams[index % refData.teams.length] || ''),
          lastLogin: null, // Not tracked yet
          createdAt: user.created_at || new Date().toISOString(),
          permissions: getRolePermissions(user.role || 'viewer')
        }
      })

      setUsers(transformedUsers)
    } catch (err) {
      console.error('Error loading users:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesRole = roleFilter === 'ALL' || user.role === roleFilter
    const matchesStatus = statusFilter === 'ALL' || user.status === statusFilter
    
    return matchesSearch && matchesRole && matchesStatus
  })

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never'
    return new Date(timestamp).toLocaleString()
  }

  const handleStatusChange = async (username, newStatus) => {
    try {
      // Update in backend
      const response = await authFetch(getLocalApiUrl(`/api/admin/users/${encodeURIComponent(username)}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: newStatus === 'active' })
      })
      
      const result = await response.json()
      if (!result.success) {
        throw new Error(result.error || 'Failed to update user status')
      }
      
      // Update local state
      setUsers(users.map(user => 
        user.username === username ? { ...user, status: newStatus } : user
      ))
    } catch (err) {
      console.error('Error updating user status:', err)
      alert('Failed to update user status: ' + err.message)
    }
  }

  const handleCreateUser = () => {
    // Redirect to User Management page for creation
    navigate('/admin/user-management')
  }

  const handleEditUser = (user) => {
    // Redirect to User Management page for editing
    navigate('/admin/user-management')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h3 className="text-red-800 font-medium mb-2">Error Loading Users</h3>
        <p className="text-red-600 text-sm">{error}</p>
        <button onClick={loadUsers} className="mt-4 btn-secondary">
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">User Management</h1>
            <p className="text-gray-600">
              Manage user accounts, roles, and permissions for the rules engine system.
            </p>
          </div>
          <button 
            onClick={handleCreateUser}
            className="btn-primary"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Add User
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Users
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or email..."
                className="input-field pl-10"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role
            </label>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="select-field"
            >
              <option value="ALL">All Roles</option>
              {roleFilterEntries.map(([key, role]) => (
                <option key={key} value={key}>{role.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="select-field"
            >
              <option value="ALL">All Status</option>
              {Object.entries(USER_STATUS).map(([key, status]) => (
                <option key={key} value={key}>{status.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* User Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {Object.entries(USER_STATUS).map(([key, status]) => {
          const count = users.filter(user => user.status === key).length
          return (
            <div key={key} className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600">{status.label}</p>
                  <p className="text-2xl font-semibold text-gray-900">{count}</p>
                </div>
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${status.color.replace('text-', 'bg-').replace('-800', '-200')}`}>
                  <UserCircleIcon className="w-6 h-6" />
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Users Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            Users ({filteredUsers.length})
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Team
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                        <UserCircleIcon className="w-6 h-6 text-gray-500" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {user.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleDisplay(user.role).color}`}>
                      {getRoleDisplay(user.role).label}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {user.team.replace('_', ' ')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${USER_STATUS[user.status].color}`}>
                      {USER_STATUS[user.status].label}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatTimestamp(user.lastLogin)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button 
                        onClick={() => setSelectedUser(user)}
                        className="text-primary-600 hover:text-primary-900"
                        title="View Details"
                      >
                        <EyeIcon className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleEditUser(user)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="Edit User"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      {user.status === 'active' ? (
                        <button 
                          onClick={() => handleStatusChange(user.username, 'inactive')}
                          className="text-red-600 hover:text-red-900"
                          title="Deactivate User"
                        >
                          <XCircleIcon className="w-4 h-4" />
                        </button>
                      ) : (
                        <button 
                          onClick={() => handleStatusChange(user.username, 'active')}
                          className="text-green-600 hover:text-green-900"
                          title="Activate User"
                        >
                          <ShieldCheckIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* User Detail Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">User Details</h3>
                <button 
                  onClick={() => setSelectedUser(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <p className="text-sm text-gray-900">{selectedUser.firstName} {selectedUser.lastName}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <p className="text-sm text-gray-900">{selectedUser.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Permissions</label>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {selectedUser.permissions.map(permission => (
                      <span key={permission} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {permission.replace('_', ' ')}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Created</label>
                  <p className="text-sm text-gray-900">{formatTimestamp(selectedUser.createdAt)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal - Redirect to User Management */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Create New User</h3>
              <p className="text-sm text-gray-600 mb-4">Redirecting to User Management page...</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Users